import { useState } from "react";
import { Button, isPasskeySupported, registerPasskey } from "@shop-ledger/ui-components";
import { usePlatformAuth } from "../../auth/PlatformAuthContext";
import * as webauthnClient from "../../api/platformWebauthnClient";
import { StatusBanner } from "../../components/StatusBanner";
import { MockBadge } from "../../components/MockBadge";
import { useAsyncData } from "../../lib/useAsyncData";
import { PLATFORM_API_MODE } from "../../env";
import { loadPlatformSession } from "../../lib/storage";

/** An operator's own account settings — passkey management. A real WebAuthn ceremony has
 * no honest mock-mode equivalent (see `platformWebauthnClient.ts`), so this whole page is
 * badged unavailable in mock mode rather than faking browser cryptography. */
export function MyAccountPage() {
  const { operator } = usePlatformAuth();
  const isRealMode = PLATFORM_API_MODE !== "mock";

  const credentialsState = useAsyncData(async () => {
    if (!isRealMode) return [];
    const session = loadPlatformSession();
    if (!session) return [];
    return webauthnClient.webauthnListCredentials(session.access_token);
  }, [isRealMode]);

  const [addPending, setAddPending] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  async function handleAddPasskey() {
    setAddError(null);
    setAddPending(true);
    try {
      const session = loadPlatformSession();
      if (!session) throw new Error("Not signed in");
      const { session_id, options } = await webauthnClient.webauthnRegisterBegin(session.access_token);
      const credential = await registerPasskey(options);
      await webauthnClient.webauthnRegisterComplete(
        session.access_token,
        session_id,
        credential as Record<string, unknown>,
      );
      credentialsState.reload();
    } catch (error) {
      setAddError(
        error instanceof Error && error.name === "PasskeyCancelledError"
          ? "Passkey setup was cancelled or timed out."
          : error instanceof Error
            ? error.message
            : "Could not add a passkey.",
      );
    } finally {
      setAddPending(false);
    }
  }

  async function handleRemove(credentialId: string) {
    setRemovingId(credentialId);
    try {
      const session = loadPlatformSession();
      if (!session) throw new Error("Not signed in");
      await webauthnClient.webauthnDeleteCredential(session.access_token, credentialId);
      credentialsState.reload();
    } catch (error) {
      setAddError(error instanceof Error ? error.message : "Could not remove that passkey.");
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">My account</h1>
          <p className="page__subtitle">{operator?.email}</p>
        </div>
      </div>

      <div className="panel">
        <h2 className="panel__title">
          Passkeys <MockBadge live={isRealMode} label={isRealMode ? "live" : "not available"} title="A real WebAuthn ceremony has no honest mock-mode equivalent." />
        </h2>
        <p className="muted" style={{ marginBottom: 14 }}>
          Sign in with Face ID, a fingerprint, Windows Hello, or a security key instead of typing your password —
          MFA is still required afterward for this realm.
        </p>

        {!isRealMode ? (
          <StatusBanner kind="info">Switch to real mode to manage passkeys.</StatusBanner>
        ) : !isPasskeySupported() ? (
          <StatusBanner kind="info">This browser doesn't support passkeys.</StatusBanner>
        ) : (
          <>
            {addError ? <StatusBanner kind="error">{addError}</StatusBanner> : null}
            {credentialsState.loading ? (
              <p className="muted">Loading…</p>
            ) : credentialsState.data && credentialsState.data.length > 0 ? (
              <ul className="checklist" style={{ marginBottom: 14 }}>
                {credentialsState.data.map((cred) => (
                  <li key={cred.id} className="checklist__item">
                    <span style={{ flex: 1 }}>
                      <strong>{cred.nickname}</strong>
                      <br />
                      <span className="muted" style={{ fontSize: "0.78rem" }}>
                        Added {new Date(cred.created_at).toLocaleDateString()}
                        {cred.last_used_at ? ` · last used ${new Date(cred.last_used_at).toLocaleDateString()}` : ""}
                      </span>
                    </span>
                    <Button
                      type="button"
                      variant="danger"
                      size="md"
                      disabled={removingId === cred.id}
                      onClick={() => void handleRemove(cred.id)}
                    >
                      {removingId === cred.id ? "Removing…" : "Remove"}
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted" style={{ marginBottom: 14 }}>
                No passkeys yet.
              </p>
            )}
            <Button type="button" variant="primary" size="md" disabled={addPending} onClick={() => void handleAddPasskey()}>
              {addPending ? "Waiting for your passkey…" : "Add a passkey"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
